<template>
    <div class="general-modal">
        <BaseModal :padding="isMobileOrTablet ? MODAL_PADDING_MOBILE : MODAL_PADDING_DESKTOP"
                   maxWidth="450px"
                   maxHeight="500px"
                   useContentFullHeight
                   useClickOutsideClose
                   @close="handleClose">
            <template #header/>
            <template #content>
                <div class="general-modal__content general-modal__content--justify-center">
                    <div class="general-modal__image">
                        <img :src="asset_cdn('assets/missions/hearts-recovered.svg')"
                             :alt="getTrans('quests-dashboard.yourHeartsHaveRecovered', 'Your hearts have recovered')"
                             :title="getTrans('quests-dashboard.yourHeartsHaveRecovered', 'Your hearts have recovered')"
                             width="120"
                             height="145">
                    </div>
                    <div class="general-modal__subtitle">
                        <Translate translationKey="quests-dashboard.heartRecoveryTitle"
                                   :replacements="{number: heartsRecovered}"
                                   :isPlural="heartsRecovered !== 1" />
                    </div>
                    <div class="hearts">
                        <div v-for="index in 5"
                             :key="'slot-' + index"
                             class="heart-slot">
                            <img class="heart"
                                 :src="asset_cdn('assets/missions/heart-empty.svg')"
                                 :alt="getTrans('quests-dashboard.emptyHeart', 'Empty heart')"
                                 :title="getTrans('quests-dashboard.emptyHeart', 'Empty heart')">
                            <img v-if="index <= previousHeartCount"
                                 class="heart heart-full"
                                 :src="asset_cdn('assets/missions/heart-full.svg')"
                                 :alt="getTrans('quests-dashboard.fullHeart', 'Full heart')"
                                 :title="getTrans('quests-dashboard.fullHeart', 'Full heart')">
                            <img v-if="index > previousHeartCount && index <= currentHeartCount"
                                 :class="`heart heart-animation heart-${ index }`"
                                 :src="asset_cdn('assets/missions/heart-full.svg')"
                                 :alt="getTrans('quests-dashboard.fullHeart', 'Full heart')"
                                 :title="getTrans('quests-dashboard.fullHeart', 'Full heart')">
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="general-modal__actions">
                    <span class="general-modal__action general-modal__action--confirm">
                        <BaseButton @click="handleClose">
                            <Translate translationKey="quests-dashboard.keepGaining"
                                       fallback="Keep Gaining" />
                        </BaseButton>
                    </span>
                </div>
            </template>
        </BaseModal>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import BaseModal from '@/js/components/BaseModal.vue';
    import { ModalEnum, useModalStore } from '@/js/store/quests/game/modules/ModalStore';
    import { useModalActive } from '@/js/composables/useModalActive';
    import { useDeviceSize } from '@/js/composables/useDeviceSize';
    import BaseButton from '@/js/components/BaseButton.vue';
    import { asset_cdn } from '@/js/helpers/helpers';
    import { getTrans } from '@/js/helpers/getTrans';
    import { MODAL_PADDING_DESKTOP, MODAL_PADDING_MOBILE } from '@/js/quests/global/constants/modal';
    import { Notification } from '@/js/quests/dashboard/types/types';
    import { NotificationModalTypeEnum } from '@/js/quests/dashboard/types/enums';
    import { useUserStore } from '@/js/store/quests/dashboard/UserStore';

    const modalStore = useModalStore();
    const userStore = useUserStore();
    const { isMobileOrTablet } = useDeviceSize();

    const notification = ref();
    const previousHeartCount = computed(() => notification.value?.data?.previous_heart_count ?? 0);
    const currentHeartCount = computed(() => notification.value?.data?.current_heart_count ?? 0);
    const heartsRecovered = computed(() => (currentHeartCount.value === 0 ? 0 : currentHeartCount.value - previousHeartCount.value));

    const handleClose = () => {
        modalStore.toggleModal(ModalEnum.HEART_RECOVERY, false);
    };

    useModalActive();

    watch(
        () => modalStore.modals[ModalEnum.HEART_RECOVERY],
        (newValue) => {
            if (newValue) {
                const allNotifications: Notification[] = userStore?.notifications ?? [];
                const filteredNotifications = allNotifications.filter(
                    (notification) => notification.type === NotificationModalTypeEnum.HeartRecovered,
                );
                notification.value = filteredNotifications.length > 0 ? filteredNotifications[0] : null;
            }
        },
        { immediate: true },
    );
</script>

<style scoped lang="scss">
@use "@/sass/design-system/variables" as variables;
@use "@/js/quests/dashboard/sass/mixins/_modal" as modalMixins;

.general-modal {
    @include modalMixins.modal;

    &__image {
        img {
            width: 120px;
            height: 145px;
        }
    }

    &__subtitle {
        color: variables.$baseWhite;
    }
}

.hearts {
    position: relative;
    display: flex;
    width: 100%;
    justify-content: center;

    img {
        max-width: 50px;
    }

    .heart-slot {
        position: relative;
        display: flex;
        width: 100%;
        max-width: 65px;
        min-height: 65px;
        align-items: center;
        justify-content: center;

        .heart {
            position: absolute;
            width: 100%;
        }

        .heart-full {
            transform: scale(1.05);
        }

        .heart-animation {
            animation: heartPopIn 1s forwards;
        }
    }

    @for $i from 1 through 5 {
        .heart-animation.heart-#{$i} {
            animation-delay: 0.6s + ($i - 1) * 0.3s;
            opacity: 0;
        }
    }
}

@keyframes heartPopIn {
    0% {
        opacity: 0;
        transform: scale(0);
    }
    100% {
        opacity: 1;
        transform: scale(1.01);
    }
}
</style>
